import React, { useCallback, useEffect, useState } from 'react'
import { useDispatch } from 'react-redux'
import { Link, useSearchParams } from 'react-router-dom'
import { toast } from 'react-toastify'

import DateRange from '../common/date-range'
import { getItemNames, getPriceAnalysis } from '../../redux/actions/itemAction'
import { formatDay, formatMoney, getErrorMessage } from '../../utils'

const COLUMNS = ['S.No.', 'Item Name', 'Company Name', 'Mobile', 'Invoice Date', 'Address', 'Category', 'Quantity', 'Price', 'Status']

// Same columns as Item Manager
const ItemRow = ({ item, index, className = '' }) => (
    <tr className={className}>
        <td>{index + 1}</td>
        <td>{item.name}</td>
        <td>{item.companyName || '-'}</td>
        <td className="text-nowrap">{item.mobile || '-'}</td>
        <td className="text-nowrap">{formatDay(item.invoiceDate || item.createdAt)}</td>
        <td className="AddressCell" title={item.address}>{item.address || '-'}</td>
        <td>{item.category}</td>
        <td>{item.quantity}</td>
        <td className="text-nowrap">{formatMoney(item.price)}</td>
        <td><span className={item.status === 'Active' ? 'Green' : 'Red'}>{item.status}</span></td>
    </tr>
)

const ItemTable = ({ items, rowClass, empty }) => (
    <div className="TableList">
        <table>
            <thead>
                <tr>{COLUMNS.map(c => <th key={c}>{c}</th>)}</tr>
            </thead>
            <tbody>
                {items.map((item, i) => <ItemRow key={item._id} item={item} index={i} className={rowClass} />)}
                {!items.length && <tr><td colSpan={COLUMNS.length}>{empty}</td></tr>}
            </tbody>
        </table>
    </div>
)

const PriceAnalysis = () => {
    const dispatch = useDispatch()
    const [params, setParams] = useSearchParams()
    const name = params.get('name') || ''

    const [names, setNames] = useState([])
    const [from, setFrom] = useState('')
    const [to, setTo] = useState('')
    const [data, setData] = useState(null)
    const [loading, setLoading] = useState(false)
    const [show, setShow] = useState(null) // 'min' | 'max' | null

    useEffect(() => {
        dispatch(getItemNames()).then(res => setNames(res.data)).catch(() => {})
    }, [dispatch])

    const load = useCallback(() => {
        if (!name) return setData(null)
        if (from && to && from > to) return toast.error('"From" date must be before "To" date')
        setLoading(true)
        dispatch(getPriceAnalysis({ name, from: from || undefined, to: to || undefined }))
            .then(res => setData(res.data))
            .catch(err => toast.error(getErrorMessage(err)))
            .finally(() => setLoading(false))
    }, [dispatch, name, from, to])

    useEffect(() => { load() }, [load])
    useEffect(() => { setShow(null) }, [name, from, to])

    const rows = data?.result || []
    const nameOptions = names.some(n => n.name.toLowerCase() === name.toLowerCase()) || !name ? names : [{ name, count: 0 }, ...names]
    const resultItems = show === 'min' ? data?.minItems : show === 'max' ? data?.maxItems : null

    return (
        <>
            <div className="Small-Wrapper">
                <div className="TitleBox">
                    <h2>Price Analysis{name ? ` — ${name}` : ''}</h2>
                    <Link className="TitleLink" to="/item-manager"><i className="fa fa-arrow-left"></i> Item Manager</Link>
                </div>

                <div className="Filter">
                    <div className="form-group">
                        <label>Item Name</label>
                        <select className="form-control" value={nameOptions.find(n => n.name.toLowerCase() === name.toLowerCase())?.name || ''}
                            onChange={e => setParams(e.target.value ? { name: e.target.value } : {})}>
                            <option value="">— Select item —</option>
                            {nameOptions.map(n => <option key={n.name} value={n.name}>{n.name}{n.count ? ` (${n.count})` : ''}</option>)}
                        </select>
                    </div>
                    <DateRange from={from} to={to} onChange={(f, t) => { setFrom(f); setTo(t) }} />
                    <div className="form-group">
                        <label>&nbsp;</label>
                        <button className="Button Cancel" title="Clear dates" onClick={() => { setFrom(''); setTo('') }}>
                            <i className={`fa fa-refresh ${loading ? 'fa-spin' : ''}`}></i>
                        </button>
                    </div>
                </div>

                {!name ? (
                    <p className="text-muted">Select an item to compare its prices across vendors and invoices.</p>
                ) : (
                    <>
                        <ItemTable items={rows} empty={loading ? 'Loading...' : 'No items with this name in the selected date range.'} />

                        <div className="ListFooter PriceFooter">
                            <span>
                                Total: {data?.total || 0}
                                {data?.avgPrice != null && <> · Average price: <b>{formatMoney(data.avgPrice)}</b></>}
                                {data && data.pricedCount < data.total && <small className="text-muted"> · {data.total - data.pricedCount} without a price not compared</small>}
                            </span>
                            <div className="PriceButtons">
                                <button className={`Button MinBtn ${show === 'min' ? 'Active' : ''}`} disabled={!data?.pricedCount} onClick={() => setShow(show === 'min' ? null : 'min')}>
                                    <i className="fa fa-arrow-down"></i> Min Price
                                </button>
                                <button className={`Button MaxBtn ${show === 'max' ? 'Active' : ''}`} disabled={!data?.pricedCount} onClick={() => setShow(show === 'max' ? null : 'max')}>
                                    <i className="fa fa-arrow-up"></i> Max Price
                                </button>
                            </div>
                        </div>
                    </>
                )}
            </div>

            {resultItems && (
                <div className={`Small-Wrapper PriceResult PriceResult-${show}`}>
                    <div className="TitleBox">
                        <h2>
                            {show === 'min' ? 'Minimum' : 'Maximum'} Price: {formatMoney(show === 'min' ? data.minPrice : data.maxPrice)}
                        </h2>
                        <small>{resultItems.length} item{resultItems.length === 1 ? '' : 's'}{from || to ? ` · ${from ? formatDay(from) : 'start'} to ${to ? formatDay(to) : 'today'}` : ''}</small>
                    </div>
                    <ItemTable items={resultItems} rowClass={show === 'min' ? 'MinRow' : 'MaxRow'} empty="-" />
                </div>
            )}
        </>
    )
}

export default PriceAnalysis
